import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { sky } from './skyState';

// ── HEADLIGHTS ───────────────────────────────────────────────────────────────
//
// Moving light is most of what makes a city at night feel alive: white pairs
// coming toward you, red pairs going away, and the wash of a car's beams
// sliding along the asphalt ahead of it. Traffic had none of it — dark cars
// gliding through lit streets.
//
// Fitted to each car from its own geometry: the lamps go on its front and back
// faces at its own width and height, so a Lotus and anything added to cars.json
// later both come out right without numbers per model.
//
// Shared across every car: one lamp material (white front, red back by vertex
// colour) and one beam material, so night/day is two uniform writes for the
// whole fleet. Two draw calls per car.

let SHARED = null;
function shared() {
  if (SHARED) return SHARED;
  const lamp = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: true });
  const c = document.createElement('canvas');
  c.width = 64; c.height = 128;
  const g = c.getContext('2d');
  // A beam's footprint: bright near the car, fanning and fading down the road.
  const grd = g.createRadialGradient(32, 118, 2, 32, 70, 70);
  grd.addColorStop(0, 'rgba(255,248,230,1)');
  grd.addColorStop(0.35, 'rgba(255,240,215,0.55)');
  grd.addColorStop(1, 'rgba(255,240,215,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const beam = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2, toneMapped: true, fog: false,
  });
  SHARED = { lamp, beam, tex, users: 0 };
  return SHARED;
}

// Night → day, once per frame for every car at once.
let lastFrame = -1;
function drive(frame) {
  if (frame === lastFrame || !SHARED) return;
  lastFrame = frame;
  const on = THREE.MathUtils.smoothstep(sky.night, 0.15, 0.55);
  SHARED.lamp.color.setScalar(0.35 + on * 9);
  SHARED.beam.opacity = on * 0.55;
  SHARED.beam.visible = on > 0.01;
}

function measure(root, frame) {
  // The car's box in the frame of `frame` (the moving group), from its vertices.
  const box = new THREE.Box3();
  const inv = new THREE.Matrix4().copy(frame.matrixWorld).invert();
  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry?.attributes?.position || o.userData.carLight) return;
    m.multiplyMatrices(inv, o.matrixWorld);
    const p = o.geometry.attributes.position;
    const step = Math.max(1, Math.floor(p.count / 1500));
    for (let i = 0; i < p.count; i += step) box.expandByPoint(v.fromBufferAttribute(p, i).applyMatrix4(m));
  });
  return box.isEmpty() ? null : box;
}

/**
 * Lights for one car. `carRef` is the group that moves (its local +z is the
 * direction of travel); `bodyRef` is whatever holds the car model.
 */
export default function CarLights({ carRef, bodyRef, lit = true }) {
  const mats = useMemo(shared, []);
  useEffect(() => { mats.users += 1; return () => { mats.users -= 1; }; }, [mats]);
  const [fit, setFit] = useState(null);
  const tries = useRef(0);

  useFrame(({ clock }) => {
    drive(clock.elapsedTime);
    if (fit || !carRef.current || !bodyRef.current) return;
    // The model streams in; keep looking for a few seconds after it lands.
    if (++tries.current % 15) return;
    const box = measure(bodyRef.current, carRef.current);
    if (!box) return;
    const size = box.getSize(new THREE.Vector3());
    if (size.z < 0.5 || size.x < 0.3) return;
    setFit({ min: box.min.clone(), max: box.max.clone(), size });
  });

  const geo = useMemo(() => {
    if (!fit) return null;
    const { min, max, size } = fit;
    const r = Math.max(0.035, size.x * 0.05);
    const y = min.y + size.y * 0.36;
    const parts = [];
    const lamp = (x, z, col) => {
      const s = new THREE.SphereGeometry(r, 8, 6);
      s.scale(1.3, 0.75, 0.45);
      s.translate(x, y, z);
      const n = s.attributes.position.count;
      const cols = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { cols[i * 3] = col[0]; cols[i * 3 + 1] = col[1]; cols[i * 3 + 2] = col[2]; }
      s.setAttribute('color', new THREE.BufferAttribute(cols, 3));
      parts.push(s.toNonIndexed());
    };
    const hx = size.x * 0.33;
    const front = max.z - r * 0.2; const back = min.z + r * 0.2;
    lamp(-hx, front, [1, 0.97, 0.88]); lamp(hx, front, [1, 0.97, 0.88]);
    lamp(-hx, back, [1, 0.05, 0.03]); lamp(hx, back, [1, 0.05, 0.03]);
    const lamps = mergeGeometries(parts);
    // The beam's footprint on the road, reaching ~1.6 car-lengths ahead.
    const reach = size.z * 1.6;
    const beam = new THREE.PlaneGeometry(size.x * 1.7, reach);
    beam.rotateX(-Math.PI / 2);
    beam.translate(0, min.y + 0.03, max.z + reach / 2 - size.z * 0.1);
    return { lamps, beam };
  }, [fit]);
  useEffect(() => () => { geo?.lamps.dispose(); geo?.beam.dispose(); }, [geo]);

  if (!geo) return null;
  return (
    <group visible={lit}>
      <mesh geometry={geo.lamps} material={mats.lamp} userData={{ carLight: true, noShadow: true }} />
      <mesh geometry={geo.beam} material={mats.beam} renderOrder={2} userData={{ carLight: true, noShadow: true }} />
    </group>
  );
}
